import { useQuery } from '@tanstack/react-query';
import {
  FRANCHISE_MOVEMENT_LABELS,
  FRANCHISE_PAGE_SIZE,
  FRANCHISE_ORDER_STATUS_LABELS,
  FISCAL_KIND_LABELS,
  type FiscalDocumentDTO,
  type FranchiseAccountDTO,
  type FranchiseFiscalRef,
  type FranchiseInvoiceDTO,
  type FranchiseMovementDTO,
  type FranchiseOrderDTO,
  type FranchiseOrderStatus,
  type ProductDTO,
} from '@bifeai/shared';
import { Badge, Button, EmptyState, Input, Select, useToast, type Tone } from '@bifeai/ui';
import { ChevronLeft, ChevronRight, FileText, Plus, Printer, RotateCw, Trash2, Truck } from 'lucide-react';
import { useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import { printFranchiseInvoice } from '../../lib/franchise-invoice-print';
import { formatDate, formatDateTime, formatMoney } from '../../lib/format';
import { formatQty, fromInternalQty, priceUnit, toInternalQty, centsToPesosInput, pesosToCents } from '../../lib/units';

export const ORDER_TONE: Record<FranchiseOrderStatus, Tone> = { REQUESTED: 'warning', SENT: 'success', CANCELED: 'neutral' };

/** Desde el lado de la franquicia: lo que la marca envió, ella lo recibió. */
const FRANCHISE_SIDE_LABELS: Record<FranchiseOrderStatus, string> = {
  REQUESTED: 'Falta que la marca lo envíe',
  SENT: 'Recibido',
  CANCELED: 'Cancelado',
};

export function OrderStatus({ status, side = 'brand' }: { status: FranchiseOrderStatus; side?: 'brand' | 'franchise' }) {
  return <Badge tone={ORDER_TONE[status]}>{(side === 'franchise' ? FRANCHISE_SIDE_LABELS : FRANCHISE_ORDER_STATUS_LABELS)[status]}</Badge>;
}

/** Cómo se ve cada movimiento de la cuenta corriente: un pago no es lo mismo que una nota de crédito. */
export function movementLabel(m: FranchiseMovementDTO): string {
  if (m.kind === 'ADJUSTMENT') {
    if (m.fiscal?.kind === 'CREDIT_NOTE') return 'Nota de crédito';
    if (m.fiscal?.kind === 'DEBIT_NOTE') return 'Nota de débito';
    return m.amountCents < 0 ? 'Descuento' : 'Cargo';
  }
  return FRANCHISE_MOVEMENT_LABELS[m.kind];
}

/** Período "2026-09" → "septiembre 2026". */
export function periodLabel(p: string): string {
  const [y, m] = p.split('-').map(Number) as [number, number];
  return new Intl.DateTimeFormat('es-AR', { month: 'long', year: 'numeric' }).format(new Date(y, m - 1, 15));
}

/** Productos que llevan stock (los que se pueden pedir o mandar). */
export function useStockProducts() {
  return useQuery({
    queryKey: ['products', 'stock-only'],
    queryFn: async () => (await api.get<ProductDTO[]>('/products')).filter((p) => p.tracksStock && p.isActive),
    staleTime: 60_000,
  });
}

export interface DraftLine {
  key: string;
  productId: string;
  qty: string;
  price: string;
}

export const newLine = (): DraftLine => ({ key: Math.random().toString(36).slice(2), productId: '', qty: '', price: '' });

/** Renglones de un pedido o envío: producto, cantidad y (si corresponde) precio. */
export function LinesEditor({ lines, onChange, withPrice }: { lines: DraftLine[]; onChange: (l: DraftLine[]) => void; withPrice: boolean }) {
  const products = useStockProducts();
  const set = (k: string, patch: Partial<DraftLine>) => onChange(lines.map((l) => (l.key === k ? { ...l, ...patch } : l)));
  return (
    <div className="bf-form" style={{ gap: 8 }}>
      <div className={`bf-lines-head${withPrice ? '' : ' no-price'}`}>
        <span>Producto</span>
        <span>Cantidad</span>
        {withPrice && <span>Precio ($ por kg o u.)</span>}
        <span />
      </div>
      {lines.map((l) => {
        const p = products.data?.find((x) => x.id === l.productId);
        return (
          <div key={l.key} className={`bf-lines-row${withPrice ? '' : ' no-price'}`}>
            <Select aria-label="Producto" value={l.productId} onChange={(e) => set(l.key, { productId: e.target.value })}>
              <option value="">{products.isPending ? 'Cargando…' : 'Elegí un producto'}</option>
              {products.data?.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
            </Select>
            <Input
              aria-label="Cantidad"
              inputMode="decimal"
              placeholder={p?.unitType === 'WEIGHT' ? 'Kilos' : 'Unidades'}
              value={l.qty}
              onChange={(e) => set(l.key, { qty: e.target.value })}
            />
            {withPrice && (
              <Input
                aria-label="Precio"
                inputMode="decimal"
                placeholder={p ? `$ ${priceUnit(p.unitType)}` : '$'}
                value={l.price}
                onChange={(e) => set(l.key, { price: e.target.value })}
              />
            )}
            <Button size="sm" variant="ghost" icon={<Trash2 />} aria-label="Quitar" onClick={() => onChange(lines.filter((x) => x.key !== l.key))} />
          </div>
        );
      })}
      <div>
        <Button size="sm" icon={<Plus />} onClick={() => onChange([...lines, newLine()])}>
          Agregar producto
        </Button>
      </div>
    </div>
  );
}

export function parseLines(lines: DraftLine[], products: ProductDTO[] | undefined, withPrice: boolean) {
  const out: { productId: string; qty: number; unitPriceCents: number }[] = [];
  for (const l of lines) {
    const p = products?.find((x) => x.id === l.productId);
    if (!p) return null;
    const qty = toInternalQty(p.unitType, l.qty);
    const price = withPrice ? pesosToCents(l.price) : 0;
    if (!qty || qty <= 0 || price === null) return null;
    out.push({ productId: p.id, qty, unitPriceCents: price });
  }
  return out.length ? out : null;
}

export { centsToPesosInput, fromInternalQty, formatQty };

export function OrdersTable({
  orders,
  onOpen,
  showBranch,
  actionFor,
  side = 'brand',
}: {
  orders: FranchiseOrderDTO[];
  /** Quién mira: la marca (envía) o la franquicia (recibe). */
  side?: 'brand' | 'franchise';
  onOpen?: (o: FranchiseOrderDTO) => void;
  showBranch: boolean;
  /** Texto del botón de cada pedido (null = sin botón). */
  actionFor?: (o: FranchiseOrderDTO) => string | null;
}) {
  const label = actionFor ?? ((o: FranchiseOrderDTO) => (o.status === 'REQUESTED' ? 'Preparar envío' : 'Ver'));
  if (!orders.length) return <EmptyState icon={<Truck />} title="Sin pedidos" text="Los pedidos y envíos de mercadería aparecen acá." />;
  return (
    <div className="bf-table-wrap">
      <table className="bf-table">
        <thead>
          <tr>
            <th>N.º</th>
            {showBranch && <th>Franquicia</th>}
            <th>Fecha</th>
            <th>Productos</th>
            <th>Origen</th>
            <th>Tipo</th>
            <th className="bf-num">Total</th>
            <th>Estado</th>
            {onOpen && <th aria-label="Acciones" />}
          </tr>
        </thead>
        <tbody>
          {orders.map((o) => (
            <tr key={o.id}>
              <td className="bf-num">#{o.number}</td>
              {showBranch && <td className="bf-cell-main">{o.branchName}</td>}
              <td className="bf-cell-sub">{formatDate(o.createdAt)}</td>
              <td>
                {o.items
                  .slice(0, 3)
                  .map((i) => `${i.productName} ${formatQty(i.unitType, i.qtySent ?? i.qtyRequested)}`)
                  .join(' · ')}
                {o.items.length > 3 && <span className="bf-cell-sub"> y {o.items.length - 3} más</span>}
              </td>
              <td className="bf-cell-sub">{o.fromBrand ? 'Lo mandó la marca' : 'Lo pidió la franquicia'}</td>
              <td className="bf-cell-sub">{o.mode === 'ORDER' ? 'A cuenta corriente' : 'Sin cargo'}</td>
              <td className="bf-num">{o.status === 'SENT' ? formatMoney(o.totalCents) : '—'}</td>
              <td>
                <OrderStatus status={o.status} side={side} />
                {o.sentAt && side === 'brand' && (
                  <div className="bf-cell-sub">{o.originBranchName ? `desde ${o.originBranchName}` : 'por la marca'}</div>
                )}
              </td>
              {onOpen && (
                <td className="bf-cell-actions">
                  {label(o) && (
                    <Button size="sm" onClick={() => onOpen(o)}>
                      {label(o)}
                    </Button>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------- facturas de la marca
/** Dónde se leen los comprobantes: la central o la franquicia. */
export type InvoiceBase = '/franchises/invoices' | '/franchise/invoices';

/** "Factura A 00003-00000012", "Nota de crédito A …". */
export function fiscalLabel(f: Pick<FranchiseFiscalRef, 'kind' | 'letter' | 'displayNumber'>): string {
  return `${FISCAL_KIND_LABELS[f.kind]} ${f.letter}${f.displayNumber ? ` ${f.displayNumber}` : ''}`;
}

/** Estado del comprobante en ARCA. */
export function FiscalStatus({ f }: { f: FranchiseFiscalRef }) {
  return (
    <>
      {f.status === 'AUTHORIZED' ? (
        <Badge tone="success">Con CAE</Badge>
      ) : f.status === 'PENDING' ? (
        <Badge tone="warning">Pendiente en ARCA</Badge>
      ) : (
        <Badge tone="danger">Rechazada</Badge>
      )}{' '}
      {f.environment === 'HOMO' && <Badge>Prueba</Badge>}
    </>
  );
}

/** Imprime (o guarda en PDF) un comprobante; si es una nota, con su factura asociada. */
export async function printInvoice(base: InvoiceBase, id: string): Promise<void> {
  const doc = await api.get<FiscalDocumentDTO>(`${base}/${id}`);
  let associated: { label: string } | null = null;
  if (doc.associatedId) {
    const a = await api.get<FiscalDocumentDTO>(`${base}/${doc.associatedId}`).catch(() => null);
    if (a) associated = { label: fiscalLabel(a) };
  }
  printFranchiseInvoice(doc, associated);
}

export function PrintInvoiceButton({ base, id, label }: { base: InvoiceBase; id: string; label?: string }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  return (
    <Button
      size="sm"
      variant="ghost"
      icon={<Printer />}
      loading={busy}
      aria-label={label ? undefined : 'Imprimir o guardar en PDF'}
      title="Imprimir o guardar en PDF"
      onClick={async () => {
        setBusy(true);
        try {
          await printInvoice(base, id);
        } catch (err) {
          toast.error('No se pudo abrir el comprobante', errorMessage(err));
        } finally {
          setBusy(false);
        }
      }}
    >
      {label}
    </Button>
  );
}

/** Comprobantes de la marca a franquicias: número, CAE, total y estado. */
export function InvoicesTable({
  invoices,
  base,
  showBranch,
  onRetry,
}: {
  invoices: FranchiseInvoiceDTO[];
  base: InvoiceBase;
  showBranch: boolean;
  /** Solo la marca: volver a pedir el CAE de un pendiente o rechazado. */
  onRetry?: (i: FranchiseInvoiceDTO) => Promise<void>;
}) {
  const [retrying, setRetrying] = useState<string | null>(null);
  if (!invoices.length)
    return (
      <EmptyState
        icon={<FileText />}
        title="Sin comprobantes"
        text="Las facturas de la marca por la mercadería vendida y las regalías aparecen acá."
      />
    );
  return (
    <div className="bf-table-wrap">
      <table className="bf-table">
        <thead>
          <tr>
            <th>Fecha</th>
            {showBranch && <th>Franquicia</th>}
            <th>Comprobante</th>
            <th>Concepto</th>
            <th className="bf-num">Total</th>
            <th>Estado</th>
            <th aria-label="Acciones" />
          </tr>
        </thead>
        <tbody>
          {invoices.map((i) => (
            <tr key={i.id}>
              <td className="bf-cell-sub">{formatDate(i.issueDate)}</td>
              {showBranch && <td className="bf-cell-main">{i.branchName}</td>}
              <td className="bf-cell-main">{fiscalLabel(i)}</td>
              <td>{i.concept}</td>
              <td className="bf-num">{i.kind === 'CREDIT_NOTE' ? `-${formatMoney(i.totalCents)}` : formatMoney(i.totalCents)}</td>
              <td>
                <FiscalStatus f={i} />
                {i.status !== 'AUTHORIZED' && i.error && <div className="bf-cell-sub">{i.error}</div>}
              </td>
              <td className="bf-cell-actions">
                {onRetry && i.status !== 'AUTHORIZED' && (
                  <Button
                    size="sm"
                    icon={<RotateCw />}
                    loading={retrying === i.id}
                    onClick={async () => {
                      setRetrying(i.id);
                      try {
                        await onRetry(i);
                      } finally {
                        setRetrying(null);
                      }
                    }}
                  >
                    Reintentar
                  </Button>
                )}
                <PrintInvoiceButton base={base} id={i.id} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AccountTable({
  account,
  base,
  onInvoice,
}: {
  account: FranchiseAccountDTO;
  base: InvoiceBase;
  /** Solo la marca: emitir la factura de un movimiento que quedó sin facturar. */
  onInvoice?: (m: FranchiseMovementDTO) => Promise<void>;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  if (!account.movements.length)
    return (
      <EmptyState
        icon={<Truck />}
        title="Sin movimientos"
        text={
          account.openingCents !== undefined
            ? 'No hay movimientos en estas fechas. Probá con otro período.'
            : 'Acá se ven la mercadería, las regalías y los pagos.'
        }
      />
    );
  const withInvoices = account.movements.some((m) => m.fiscal || m.canInvoice);
  return (
    <div className="bf-table-wrap">
      {account.openingCents !== undefined && (
        <p className="bf-cell-sub" style={{ margin: '0 0 8px' }}>
          Saldo al inicio del período: <strong>{formatMoney(account.openingCents)}</strong>
        </p>
      )}
      <table className="bf-table">
        <thead>
          <tr>
            <th>Fecha</th>
            <th>Concepto</th>
            {withInvoices && <th>Comprobante</th>}
            <th className="bf-num">Debe</th>
            <th className="bf-num">Haber</th>
            <th className="bf-num">Saldo</th>
          </tr>
        </thead>
        <tbody>
          {account.movements.map((m) => (
            <tr key={m.id}>
              <td className="bf-cell-sub">{formatDateTime(m.createdAt)}</td>
              <td>
                <Badge>{movementLabel(m)}</Badge> {m.description}
              </td>
              {withInvoices && (
                <td>
                  {m.fiscal && (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                        <span className="bf-cell-sub">{fiscalLabel(m.fiscal)}</span>
                        <PrintInvoiceButton base={base} id={m.fiscal.id} />
                      </div>
                      <FiscalStatus f={m.fiscal} />
                    </>
                  )}
                  {m.canInvoice && onInvoice && (
                    <Button
                      size="sm"
                      icon={<FileText />}
                      loading={busy === m.id}
                      onClick={async () => {
                        setBusy(m.id);
                        try {
                          await onInvoice(m);
                        } finally {
                          setBusy(null);
                        }
                      }}
                    >
                      Facturar
                    </Button>
                  )}
                  {!m.fiscal && !m.canInvoice && (m.kind === 'ORDER' || m.kind === 'ROYALTY') && <span className="bf-cell-sub">Sin factura</span>}
                </td>
              )}
              <td className="bf-num">{m.amountCents > 0 ? formatMoney(m.amountCents) : ''}</td>
              <td className="bf-num">{m.amountCents < 0 ? formatMoney(-m.amountCents) : ''}</td>
              <td className="bf-num">{formatMoney(m.balanceAfterCents)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** "La franquicia debe $ X" / "Saldo a favor" / "Al día". */
export function balanceText(cents: number): string {
  if (cents > 0) return `Debe ${formatMoney(cents)}`;
  if (cents < 0) return `A favor ${formatMoney(-cents)}`;
  return 'Al día';
}

// ---------------------------------------------------------------- fechas y páginas
const isoDay = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Filtro Desde/Hasta (por defecto, los últimos 3 meses) y página, para las listas de franquicias. */
export function useListRange(pageSize = FRANCHISE_PAGE_SIZE) {
  const [from, setFromRaw] = useState(() => isoDay(new Date(Date.now() - 90 * 86_400_000)));
  const [to, setToRaw] = useState(() => isoDay(new Date()));
  const [page, setPage] = useState(1);
  const setFrom = (v: string) => {
    setFromRaw(v);
    setPage(1);
  };
  const setTo = (v: string) => {
    setToRaw(v);
    setPage(1);
  };
  const qs = `from=${from}&to=${to}&page=${page}&pageSize=${pageSize}`;
  return { from, to, page, setFrom, setTo, setPage, qs, key: [from, to, page] as const };
}
export type ListRange = ReturnType<typeof useListRange>;

export function RangeFilter({ range }: { range: ListRange }) {
  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
        <span className="bf-cell-sub">Desde</span>
        <Input
          type="date"
          value={range.from}
          max={range.to}
          style={{ width: 160 }}
          onChange={(e) => e.target.value && range.setFrom(e.target.value)}
        />
      </label>
      <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
        <span className="bf-cell-sub">Hasta</span>
        <Input type="date" value={range.to} min={range.from} style={{ width: 160 }} onChange={(e) => e.target.value && range.setTo(e.target.value)} />
      </label>
    </span>
  );
}

/** "Anterior · Página 2 de 8 (113) · Siguiente". No se muestra si entra todo en una página. */
export function Pager({ range, total, pageSize = FRANCHISE_PAGE_SIZE }: { range: ListRange; total: number; pageSize?: number }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (pages <= 1) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, padding: '12px 16px' }}>
      <Button size="sm" icon={<ChevronLeft />} disabled={range.page <= 1} onClick={() => range.setPage(range.page - 1)}>
        Anterior
      </Button>
      <span className="bf-cell-sub">
        Página {range.page} de {pages} ({total})
      </span>
      <Button size="sm" disabled={range.page >= pages} onClick={() => range.setPage(range.page + 1)}>
        Siguiente <ChevronRight size={16} aria-hidden />
      </Button>
    </div>
  );
}
